/**
 * AICheckCatalogueService — AI-signal check catalogue (V3-6-1).
 *
 * Distinguishes generic infrastructure checks (branch protection, etc.)
 * from AI-specific checks. Generic checks are kept for evidence but
 * hidden from AI-register mapping. AI-signal checks map to register
 * items and drive findings.
 *
 * V5-0-4 (ADR-0030): entries now carry `control_id` referencing actual
 * controls from the 76-row catalogue. `register_item_ref` is retained
 * for backward-compat reads but `control_id` is the canonical mapping.
 */
const AI_SIGNAL_CHECKS = [
    {
        check_key: 'ai_oauth_grant_unsanctioned',
        category: 'ai_signal',
        display_name: 'New AI OAuth grant outside sanctioned list',
        description: 'An IdP OAuth grant was detected for an AI service not on the workspace sanctioned list.',
        control_id: 'OGCF-12',
        register_item_ref: 'RR-SUPPLY-CHAIN-001',
    },
    {
        check_key: 'supplier_ai_feature_drift',
        category: 'ai_signal',
        display_name: 'Supplier AI feature drift',
        description: 'A portfolio supplier\'s disclosure or scorecard score has changed since the last review.',
        control_id: 'OGCF-13',
        register_item_ref: 'RR-SUPPLY-CHAIN-002',
    },
    {
        check_key: 'overdue_high_tier_rereview',
        category: 'ai_signal',
        display_name: 'Overdue high-tier re-review',
        description: 'A high-risk or critical use case has passed its scheduled re-review date without action.',
        control_id: 'OGCF-1',
        register_item_ref: 'RR-GOV-001',
    },
    {
        check_key: 'model_endpoint_drift',
        category: 'ai_signal',
        display_name: 'Model endpoint drift (customer-provided)',
        description: 'A customer-registered model endpoint returned a different model version or provider than expected.',
        control_id: 'ML-7',
        register_item_ref: 'RR-TRANSPARENCY-001',
    },
];
const NON_AI_CHECKS = [
    {
        check_key: 'github_branch_protection',
        category: 'non_ai',
        display_name: 'Branch protection enabled',
        description: 'GitHub branch protection rules are configured on the default branch.',
        control_id: 'CM-2',
        register_item_ref: null,
    },
    {
        check_key: 'github_code_review_required',
        category: 'non_ai',
        display_name: 'Code review required',
        description: 'Pull request reviews are required before merge.',
        control_id: 'CM-2',
        register_item_ref: null,
    },
    {
        check_key: 'github_secret_scanning',
        category: 'non_ai',
        display_name: 'Secret scanning enabled',
        description: 'GitHub secret scanning is enabled on the repository.',
        control_id: 'AC-2',
        register_item_ref: null,
    },
    {
        check_key: 'github_codeowners',
        category: 'non_ai',
        display_name: 'CODEOWNERS file present',
        description: 'A CODEOWNERS file exists defining code ownership.',
        control_id: 'OGCF-1',
        register_item_ref: null,
    },
];
const ALL_CHECKS = [...AI_SIGNAL_CHECKS, ...NON_AI_CHECKS];
const CHECK_MAP = new Map(ALL_CHECKS.map((c) => [c.check_key, c]));
export function getCheckCatalogue() {
    return ALL_CHECKS;
}
export function getAiSignalChecks() {
    return AI_SIGNAL_CHECKS;
}
export function getNonAiChecks() {
    return NON_AI_CHECKS;
}
export function getCheckByKey(key) {
    return CHECK_MAP.get(key);
}
export function isAiSignalCheck(key) {
    return CHECK_MAP.get(key)?.category === 'ai_signal';
}
export function getAiCheckCount() {
    return AI_SIGNAL_CHECKS.length;
}
export function getNonAiCheckCount() {
    return NON_AI_CHECKS.length;
}
//# sourceMappingURL=ai-check-catalogue-service.js.map